import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Lock, Send, Unlock, Zap, ZapOff } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import type { Parametro } from '@/shared/api/tipos';

export function PoliticasDeCobro() {
  return (
    <div className="grid gap-4">
      <Grupo etiqueta="Políticas de cobro">
        <Politica
          grupo="emision"
          clave="anticipacion_permitida"
          iconoActivo={Unlock}
          iconoInactivo={Lock}
          etiqueta="Facturas anticipadas"
          textoActivo="Permitidas"
          textoInactivo="Bloqueadas"
          detalleActivo="La ventanilla puede cobrar meses que todavía no han llegado."
          detalleInactivo="Solo se cobra el mes corriente y los anteriores."
          accionActivar="Permitir"
          accionDesactivar="Bloquear"
        />
      </Grupo>

      <Grupo etiqueta="Integración SRI">
        <Politica
          grupo="sri"
          clave="sri_procesamiento_automatico"
          iconoActivo={Zap}
          iconoInactivo={ZapOff}
          etiqueta="Envío al SRI"
          textoActivo="Automático"
          textoInactivo="Manual"
          detalleActivo="Los comprobantes se transmiten solos después de cada emisión."
          detalleInactivo="Los comprobantes esperan a que alguien los transmita."
          accionActivar="Automatizar"
          accionDesactivar="Pasar a manual"
        />
      </Grupo>

      <p className="text-[11px] uppercase tracking-wide text-armazon-500">
        <Send className="mr-1 inline size-3 -translate-y-px" aria-hidden />
        Se aplican al pulsar, no al guardar el formulario.
      </p>
    </div>
  );
}

function Grupo({ etiqueta, children }: { etiqueta: string; children: React.ReactNode }) {
  return (
    <div className="grid gap-2">
      <p className="px-1 text-[10px] font-bold uppercase tracking-widest text-armazon-500">
        {etiqueta}
      </p>
      {children}
    </div>
  );
}

function Politica({
  grupo,
  clave,
  iconoActivo: IconoActivo,
  iconoInactivo: IconoInactivo,
  etiqueta,
  textoActivo,
  textoInactivo,
  detalleActivo,
  detalleInactivo,
  accionActivar,
  accionDesactivar,
}: {
  grupo: string;
  clave: string;
  iconoActivo: LucideIcon;
  iconoInactivo: LucideIcon;
  etiqueta: string;
  textoActivo: string;
  textoInactivo: string;
  detalleActivo: string;
  detalleInactivo: string;
  accionActivar: string;
  accionDesactivar: string;
}) {
  const clienteDeConsultas = useQueryClient();

  const parametros = useQuery({
    queryKey: ['parametros', grupo],
    queryFn: () => api.get<Parametro[]>(`/parametros/${grupo}`),
  });

  const cambiar = useMutation({
    mutationFn: (activo: boolean) =>
      api.put<Parametro[]>(`/parametros/${grupo}`, {
        parametros: [{ clave, valor: activo ? 'true' : 'false' }],
      }),
    onSuccess: () => clienteDeConsultas.invalidateQueries({ queryKey: ['parametros', grupo] }),
  });

  if (parametros.isPending) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-armazon-200 bg-armazon-50 px-4 py-3 text-xs uppercase text-armazon-500">
        <Loader2 className="size-3.5 animate-spin" aria-hidden />
        Cargando {etiqueta.toLowerCase()}…
      </div>
    );
  }

  const parametro = parametros.data?.find((p) => p.clave === clave);
  if (!parametro) {
    return null;
  }

  const activo = parametro.valor === true;
  const Icono = activo ? IconoActivo : IconoInactivo;

  return (
    <div
      className={[
        'flex flex-wrap items-center gap-3 rounded-xl border px-4 py-3',
        activo ? 'border-marca-300 bg-marca-50' : 'border-amber-300 bg-amber-50',
      ].join(' ')}
    >
      <span
        className={[
          'grid size-9 shrink-0 place-items-center rounded-full text-white',
          activo ? 'bg-marca-600' : 'bg-amber-500',
        ].join(' ')}
      >
        <Icono className="size-4" aria-hidden />
      </span>

      <div className="min-w-0 flex-1">
        <p
          className={`flex items-center gap-1 text-xs font-bold uppercase tracking-wide ${
            activo ? 'text-marca-900' : 'text-amber-900'
          }`}
        >

          {etiqueta} — {activo ? textoActivo : textoInactivo}
        </p>
        <p className={`text-[11px] uppercase ${activo ? 'text-marca-700' : 'text-amber-800'}`}>
          {cambiar.isError ? errorLegible(cambiar.error) : activo ? detalleActivo : detalleInactivo}
        </p>
      </div>

      <button
        type="button"
        onClick={() => cambiar.mutate(!activo)}
        disabled={cambiar.isPending}
        className={[
          'shrink-0 rounded-lg px-4 py-2 text-xs font-semibold uppercase text-white transition-colors disabled:opacity-50',
          activo ? 'bg-amber-600 hover:bg-amber-700' : 'bg-marca-600 hover:bg-marca-700',
        ].join(' ')}
      >
        {cambiar.isPending ? '…' : activo ? accionDesactivar : accionActivar}
      </button>
    </div>
  );
}

function errorLegible(causa: unknown): string {
  return causa instanceof ErrorDeApi ? causa.message : 'No se pudo cambiar la política.';
}
